/**
 * 8-Bit Audio Engine
 * Generates retro chiptune sounds using Web Audio API oscillators.
 */

// Note frequencies (A4 = 440Hz standard tuning)
const NOTE_FREQUENCIES = {
    'C3': 130.81, 'D3': 146.83, 'E3': 164.81, 'F3': 174.61, 'F#3': 185.00, 'G3': 196.00, 'A3': 220.00, 'B3': 246.94,
    'C4': 261.63, 'D4': 293.66, 'E4': 329.63, 'F4': 349.23, 'F#4': 369.99, 'G4': 392.00, 'A4': 440.00, 'B4': 493.88,
    'C5': 523.25, 'D5': 587.33, 'E5': 659.25, 'F5': 698.46, 'F#5': 739.99, 'G5': 783.99, 'A5': 880.00, 'B5': 987.77,
    'C6': 1046.50
};

// Progression: base notes for each level (0-4+ categories completed)
const PROGRESSION_BASES = ['C4', 'D4', 'E4', 'F#4', 'G4', 'A4'];

class AudioEngine {
    constructor() {
        this.context = null;
        this.masterGain = null;
        this.isInitialized = false;
        this.isMusicMuted = false;
        this.isSfxMuted = false;
        this.categoriesCompleted = 0;
        this.ambientLoop = null;
        this.ambientNodes = [];
        this.isVictoryMode = false;
        this.storageKey = 'taxonomic-audio';

        // Load preference
        this.loadPreference();

        // Store bound handlers for cleanup
        this.soundEventHandler = (e) => this.handleSoundEvent(e);
        this.visibilityHandler = () => this.handleVisibilityChange();

        // Listen for game sound events
        window.addEventListener('game:sound', this.soundEventHandler);

        // Pause/resume when tab visibility changes
        document.addEventListener('visibilitychange', this.visibilityHandler);

        // Initialize on first user interaction
        this.setupAutoInit();
    }

    /**
     * Load audio preference from localStorage
     */
    loadPreference() {
        try {
            const musicPref = localStorage.getItem(this.storageKey + '-music');
            const sfxPref = localStorage.getItem(this.storageKey + '-sfx');
            // Music defaults to OFF (muted) unless explicitly turned on
            this.isMusicMuted = musicPref !== 'on';
            // SFX defaults to ON unless explicitly turned off
            this.isSfxMuted = sfxPref === 'off';
        } catch (e) {
            this.isMusicMuted = true;  // Default music to off
            this.isSfxMuted = false;
        }
    }

    /**
     * Save audio preference to localStorage
     */
    savePreference() {
        try {
            localStorage.setItem(this.storageKey + '-music', this.isMusicMuted ? 'off' : 'on');
            localStorage.setItem(this.storageKey + '-sfx', this.isSfxMuted ? 'off' : 'on');
        } catch (e) {
            // Ignore storage errors
        }
    }

    /**
     * Set up auto-initialization on first user interaction
     */
    setupAutoInit() {
        const initOnInteraction = () => {
            if (!this.isInitialized) {
                this.init();
            }
            document.removeEventListener('click', initOnInteraction);
            document.removeEventListener('keydown', initOnInteraction);
        };

        document.addEventListener('click', initOnInteraction);
        document.addEventListener('keydown', initOnInteraction);
    }

    /**
     * Initialize the audio context
     */
    init() {
        if (this.isInitialized) return;

        try {
            this.context = new (window.AudioContext || window.webkitAudioContext)();
            this.masterGain = this.context.createGain();
            this.masterGain.connect(this.context.destination);
            this.masterGain.gain.value = 1;
            this.isInitialized = true;

            // Start ambient loop if not muted
            if (!this.isMusicMuted) {
                this.startAmbientLoop();
            }
        } catch (e) {
            console.warn('Web Audio API not supported:', e);
        }
    }

    /**
     * Toggle music mute state
     */
    toggleMusic() {
        this.isMusicMuted = !this.isMusicMuted;
        this.savePreference();

        if (this.isInitialized) {
            if (this.isMusicMuted) {
                this.stopAmbientLoop();
            } else if (!document.hidden) {
                if (this.isVictoryMode) {
                    this.startVictoryLoop();
                } else {
                    this.startAmbientLoop();
                }
            }
        }

        return !this.isMusicMuted;
    }

    /**
     * Toggle sound effects mute state
     */
    toggleSfx() {
        this.isSfxMuted = !this.isSfxMuted;
        this.savePreference();
        return !this.isSfxMuted;
    }

    /**
     * Handle tab visibility changes - pause when hidden, resume when visible
     */
    handleVisibilityChange() {
        if (!this.isInitialized) return;

        if (document.hidden) {
            // Tab is hidden - stop ambient loop
            this.stopAmbientLoop();
        } else {
            // Tab is visible - resume appropriate loop if not muted
            if (!this.isMusicMuted) {
                if (this.isVictoryMode) {
                    this.startVictoryLoop();
                } else {
                    this.startAmbientLoop();
                }
            }
        }
    }

    /**
     * Get current base frequency based on progression
     */
    getBaseFrequency() {
        const level = Math.min(this.categoriesCompleted, PROGRESSION_BASES.length - 1);
        const baseNote = PROGRESSION_BASES[level];
        return NOTE_FREQUENCIES[baseNote];
    }

    /**
     * Get frequency for a note, transposed to current progression level
     */
    getFrequency(note, octaveOffset = 0) {
        const baseFreq = NOTE_FREQUENCIES[note] || 261.63;
        const progressionMultiplier = this.getBaseFrequency() / NOTE_FREQUENCIES['C4'];
        return baseFreq * progressionMultiplier * Math.pow(2, octaveOffset);
    }

    /**
     * Set progression level (called when category completes)
     */
    setProgression(categoriesCompleted) {
        this.categoriesCompleted = categoriesCompleted;
        // Ambient loop will pick up new frequencies on next iteration
    }

    /**
     * Handle incoming sound events
     */
    handleSoundEvent(event) {
        if (!this.isInitialized) return;

        const { sound } = event.detail;

        // Music events check isMusicMuted, SFX events check isSfxMuted
        const isMusicEvent = sound === 'fanfare-victory';
        if (isMusicEvent && this.isMusicMuted) return;
        if (!isMusicEvent && this.isSfxMuted) return;

        switch (sound) {
            case 'tap':
                this.playTap();
                break;
            case 'select':
                this.playSelect();
                break;
            case 'deselect':
                this.playDeselect();
                break;
            case 'match-success':
                this.playMatchSuccess();
                break;
            case 'match-fail':
                this.playMatchFail();
                break;
            case 'category-complete':
                this.playCanterGoryComplete();
                break;
            case 'fanfare-victory':
                this.playVictoryFanfare();
                // Start victory loop after fanfare finishes
                setTimeout(() => {
                    if (!this.isMusicMuted) {
                        this.startVictoryLoop();
                    }
                }, 1200); // Fanfare is ~1120ms
                break;
            case 'dive-in':
                this.playDiveIn();
                break;
            case 'dive-out':
                this.playDiveOut();
                break;
            case 'game-ready':
                this.playGameReady();
                break;
            case 'theme-switch':
                this.playThemeSwitch();
                break;
        }
    }

    /**
     * Create an oscillator with attack/release envelope
     */
    createOscillator(type, frequency, duration, volume = 0.7, attackTime = 0.01, releaseTime = 0.05) {
        const osc = this.context.createOscillator();
        const gain = this.context.createGain();

        osc.type = type;
        osc.frequency.value = frequency;

        osc.connect(gain);
        gain.connect(this.masterGain);

        const now = this.context.currentTime;
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(volume, now + attackTime);
        gain.gain.linearRampToValueAtTime(0, now + duration - releaseTime);

        osc.start(now);
        osc.stop(now + duration);

        return { osc, gain };
    }

    /**
     * Play a sequence of notes
     */
    playSequence(notes, type = 'square', noteLength = 0.1, volume = 0.5) {
        notes.forEach((note, i) => {
            const freq = typeof note === 'number' ? note : this.getFrequency(note);
            setTimeout(() => {
                this.createOscillator(type, freq, noteLength, volume);
            }, i * noteLength * 1000 * 0.9);
        });
    }

    // ==================== SOUND EFFECTS ====================

    /**
     * Tap - Quick blip
     */
    playTap() {
        this.createOscillator('square', this.getFrequency('C4', -1), 0.05, 0.3);
    }

    /**
     * Select - Rising chirp (C→E)
     */
    playSelect() {
        const baseFreq = this.getBaseFrequency();
        this.createOscillator('square', baseFreq, 0.05, 0.4);
        setTimeout(() => {
            this.createOscillator('square', baseFreq * 1.25, 0.05, 0.4); // Major third up
        }, 50);
    }

    /**
     * Deselect - Falling chirp (E→C)
     */
    playDeselect() {
        const baseFreq = this.getBaseFrequency();
        this.createOscillator('square', baseFreq * 1.25, 0.05, 0.3);
        setTimeout(() => {
            this.createOscillator('square', baseFreq, 0.05, 0.3);
        }, 50);
    }

    /**
     * Match Success - Happy major arpeggio (C-E-G-C)
     */
    playMatchSuccess() {
        const base = this.getBaseFrequency();
        const notes = [base, base * 1.25, base * 1.5, base * 2]; // C-E-G-C
        notes.forEach((freq, i) => {
            setTimeout(() => {
                this.createOscillator('square', freq, 0.15, 0.5);
            }, i * 70);
        });
    }

    /**
     * Match Fail - Buzzy low tone with wobble
     */
    playMatchFail() {
        const osc = this.context.createOscillator();
        const gain = this.context.createGain();

        osc.type = 'sawtooth';
        osc.frequency.value = 110;

        // Add wobble
        const lfo = this.context.createOscillator();
        const lfoGain = this.context.createGain();
        lfo.frequency.value = 15;
        lfoGain.gain.value = 20;
        lfo.connect(lfoGain);
        lfoGain.connect(osc.frequency);

        osc.connect(gain);
        gain.connect(this.masterGain);

        const now = this.context.currentTime;
        gain.gain.setValueAtTime(0.4, now);
        gain.gain.linearRampToValueAtTime(0, now + 0.25);

        lfo.start(now);
        osc.start(now);
        osc.stop(now + 0.25);
        lfo.stop(now + 0.25);
    }

    /**
     * Category Complete - 4-note fanfare
     */
    playCanterGoryComplete() {
        const base = this.getBaseFrequency();
        const notes = [base, base * 1.25, base * 1.5, base * 2];

        notes.forEach((freq, i) => {
            setTimeout(() => {
                const duration = i === 3 ? 0.3 : 0.12;
                this.createOscillator('square', freq, duration, 0.5);
                // Add harmony on last note
                if (i === 3) {
                    this.createOscillator('square', freq * 1.5, duration, 0.3);
                }
            }, i * 120);
        });
    }

    /**
     * Victory Fanfare - Full 8-note jingle with harmony
     */
    playVictoryFanfare() {
        const base = this.getBaseFrequency();
        // Classic victory melody
        const melody = [
            base, base * 1.25, base * 1.5, base,
            base * 1.25, base * 1.5, base * 1.75, base * 2
        ];
        const harmony = [
            base * 0.5, base * 0.625, base * 0.75, base * 0.5,
            base * 0.625, base * 0.75, base * 0.875, base
        ];

        melody.forEach((freq, i) => {
            setTimeout(() => {
                const duration = i === 7 ? 0.4 : 0.12;
                const vol = i === 7 ? 0.7 : 0.5;
                this.createOscillator('square', freq, duration, vol);
                this.createOscillator('triangle', harmony[i], duration, 0.3);
            }, i * 140);
        });
    }

    /**
     * Dive In - Descending sweep
     */
    playDiveIn() {
        const osc = this.context.createOscillator();
        const gain = this.context.createGain();

        osc.type = 'square';
        osc.connect(gain);
        gain.connect(this.masterGain);

        const now = this.context.currentTime;
        const highFreq = this.getBaseFrequency() * 4;
        const lowFreq = this.getBaseFrequency() * 0.5;

        osc.frequency.setValueAtTime(highFreq, now);
        osc.frequency.exponentialRampToValueAtTime(lowFreq, now + 0.4);

        gain.gain.setValueAtTime(0.3, now);
        gain.gain.linearRampToValueAtTime(0, now + 0.4);

        osc.start(now);
        osc.stop(now + 0.4);
    }

    /**
     * Dive Out - Ascending sweep
     */
    playDiveOut() {
        const osc = this.context.createOscillator();
        const gain = this.context.createGain();

        osc.type = 'square';
        osc.connect(gain);
        gain.connect(this.masterGain);

        const now = this.context.currentTime;
        const lowFreq = this.getBaseFrequency() * 0.5;
        const highFreq = this.getBaseFrequency() * 4;

        osc.frequency.setValueAtTime(lowFreq, now);
        osc.frequency.exponentialRampToValueAtTime(highFreq, now + 0.4);

        gain.gain.setValueAtTime(0.3, now);
        gain.gain.linearRampToValueAtTime(0, now + 0.4);

        osc.start(now);
        osc.stop(now + 0.4);
    }

    /**
     * Game Ready - 3-note ascending chime
     */
    playGameReady() {
        const base = this.getBaseFrequency();
        const notes = [base, base * 1.25, base * 1.5];

        notes.forEach((freq, i) => {
            setTimeout(() => {
                this.createOscillator('triangle', freq, 0.2, 0.4);
            }, i * 150);
        });
    }

    /**
     * Theme Switch - Quick click
     */
    playThemeSwitch() {
        this.createOscillator('square', 800, 0.03, 0.2);
        setTimeout(() => {
            this.createOscillator('square', 1000, 0.03, 0.15);
        }, 30);
    }

    // ==================== AMBIENT LOOP ====================

    /**
     * Start the ambient background loop
     */
    startAmbientLoop() {
        if (this.ambientLoop) return;

        const playBar = (barIndex) => {
            if (this.isMusicMuted || !this.isInitialized) {
                this.ambientLoop = null;
                return;
            }

            const base = this.getBaseFrequency();
            const beatDuration = 0.6; // 100 BPM = 600ms per beat

            // Chord progressions for each bar
            const chords = [
                [base, base * 1.5],           // C, G (I)
                [base, base * 1.5],           // C, G (I)
                [base * 0.833, base * 1.25],  // Am (vi) - A and E
                [base * 1.333, base * 1.5]    // F, G (IV, V)
            ];

            const chord = chords[barIndex % 4];

            // Bass note (triangle, quiet)
            this.createOscillator('triangle', chord[0] * 0.5, beatDuration * 0.9, 0.15);

            // Arpeggio pattern (square, very quiet)
            const arpNotes = [chord[0], chord[1], chord[0] * 2, chord[1]];
            arpNotes.forEach((freq, i) => {
                setTimeout(() => {
                    if (!this.isMusicMuted) {
                        this.createOscillator('square', freq, 0.1, 0.08);
                    }
                }, i * (beatDuration * 250));
            });

            // Schedule next bar
            this.ambientLoop = setTimeout(() => {
                playBar((barIndex + 1) % 4);
            }, beatDuration * 1000);
        };

        playBar(0);
    }

    /**
     * Stop the ambient loop
     */
    stopAmbientLoop() {
        if (this.ambientLoop) {
            clearTimeout(this.ambientLoop);
            this.ambientLoop = null;
        }
    }

    /**
     * Start the victory celebration loop - upbeat major key melody
     */
    startVictoryLoop() {
        if (this.ambientLoop) {
            this.stopAmbientLoop();
        }
        this.isVictoryMode = true;

        const playVictoryBar = (barIndex) => {
            if (this.isMusicMuted || !this.isInitialized || !this.isVictoryMode) {
                this.ambientLoop = null;
                return;
            }

            const base = NOTE_FREQUENCIES['C5']; // Higher, brighter key
            const beatDuration = 0.4; // Faster tempo (150 BPM)

            // Upbeat major progression
            const progressions = [
                { bass: base, chord: [base, base * 1.25, base * 1.5] },       // C major
                { bass: base * 0.75, chord: [base * 0.75, base, base * 1.25] }, // G major
                { bass: base * 0.833, chord: [base * 0.833, base * 1.042, base * 1.25] }, // Am
                { bass: base * 0.667, chord: [base * 0.667, base * 0.833, base] }  // F major
            ];

            const prog = progressions[barIndex % 4];

            // Bouncy bass (triangle)
            this.createOscillator('triangle', prog.bass * 0.5, beatDuration * 0.3, 0.2);
            setTimeout(() => {
                if (!this.isMusicMuted && this.isVictoryMode) {
                    this.createOscillator('triangle', prog.bass * 0.5, beatDuration * 0.3, 0.15);
                }
            }, beatDuration * 500);

            // Happy arpeggio pattern (square)
            prog.chord.forEach((freq, i) => {
                setTimeout(() => {
                    if (!this.isMusicMuted && this.isVictoryMode) {
                        this.createOscillator('square', freq, 0.15, 0.12);
                    }
                }, i * (beatDuration * 200));
            });

            // High sparkle note on beats 2 and 4
            if (barIndex % 2 === 1) {
                setTimeout(() => {
                    if (!this.isMusicMuted && this.isVictoryMode) {
                        this.createOscillator('square', base * 2, 0.08, 0.08);
                    }
                }, beatDuration * 250);
            }

            // Schedule next bar
            this.ambientLoop = setTimeout(() => {
                playVictoryBar((barIndex + 1) % 4);
            }, beatDuration * 1000);
        };

        playVictoryBar(0);
    }

    /**
     * Stop victory loop and return to normal ambient
     */
    stopVictoryLoop() {
        this.isVictoryMode = false;
        this.stopAmbientLoop();
    }

    /**
     * Clean up event listeners and audio resources
     */
    destroy() {
        // Remove event listeners
        if (this.soundEventHandler) {
            window.removeEventListener('game:sound', this.soundEventHandler);
        }
        if (this.visibilityHandler) {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
        }

        // Stop any playing audio
        this.stopAmbientLoop();
        this.stopVictoryLoop();

        // Close audio context if it exists
        if (this.context && this.context.state !== 'closed') {
            this.context.close().catch(() => {});
        }
    }
}

// Create singleton instance
const audioEngine = new AudioEngine();

export { audioEngine, AudioEngine };
export default audioEngine;
